import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Users, CheckCircle, XCircle, Clock, Search, Trash2, X, Shield,
  RefreshCw, FileText, Sliders, AlertTriangle, Loader2, ToggleLeft, ToggleRight,
  ChevronRight, Eye, Ban, Unlock, Download, Megaphone, ScrollText, Settings2, ShieldCheck,
  LayoutDashboard, Palette, UserCheck, ShieldAlert, Timer, Database, Info, CreditCard,
  TrendingUp, Globe, LogIn, AlertCircle, CheckSquare, Square, Wifi, WifiOff,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../../utils/api';
import BillingTab from './BillingTab';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { format, formatDistanceToNow, addDays, isPast } from 'date-fns';


// ── Constants ─────────────────────────────────────────────────────────────────
const MAIN_TABS = [
  { key: 'overview',       label: 'Overview',       icon: LayoutDashboard },
  { key: 'orgs',           label: 'Organisations',  icon: Building2 },
  { key: 'announcements',  label: 'Announcements',  icon: Megaphone },
  { key: 'audit',          label: 'Audit Log',      icon: ScrollText },
  { key: 'billing',        label: 'Billing',        icon: CreditCard },
  { key: 'settings',       label: 'Settings',       icon: Settings2 },
];

const ORG_DRAWER_TABS = [
  { key: 'overview',   label: 'Overview',      icon: Info },
  { key: 'usage',      label: 'Usage & Limits', icon: TrendingUp },
  { key: 'trial',      label: 'Trial',         icon: Timer },
  { key: 'security',   label: 'Security',      icon: ShieldAlert },
  { key: 'branding',   label: 'Branding',      icon: Palette },
  { key: 'onboarding', label: 'Onboarding',    icon: UserCheck },
  { key: 'gdpr',       label: 'GDPR',          icon: Database },
];

const STATUS_TABS = [
  { value: '',                     label: 'All' },
  { value: 'pending_verification', label: 'Pending Verification' },
  { value: 'verified',             label: 'Awaiting Approval' },
  { value: 'approved',             label: 'Approved' },
  { value: 'rejected',             label: 'Rejected' },
];

const STATUS_BADGE = {
  pending_verification: { label: 'Pending Verify', cls: 'bg-amber-100 text-amber-700' },
  verified:             { label: 'Awaiting Approval', cls: 'bg-blue-100 text-blue-700' },
  approved:             { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
  rejected:             { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

// Must stay in step with MODULES and DEFAULT_OFF in backend/routes/features.js — a key
// listed here that the backend does not know is a toggle that does nothing, and a
// defaultOff that disagrees shows the wrong state before anything is saved.
// `tier` is the plan that grants it, so it is visible what an org is actually paying for.
const FEATURE_LIST = [
  // Core — every plan
  { key: 'scheduler',    label: 'Scheduler',       desc: 'Shift scheduling & rostering', tier: 'Starter' },
  { key: 'clients',      label: 'Clients',         desc: 'Participant management', tier: 'Starter' },
  { key: 'invoices',     label: 'Invoices',        desc: 'NDIS billing & invoicing', tier: 'Starter' },
  { key: 'timesheets',   label: 'Timesheets',      desc: 'Timesheet approvals', tier: 'Starter' },
  { key: 'reports',      label: 'Reports',         desc: 'Analytics & reporting hub', tier: 'Starter' },
  { key: 'leave',        label: 'Leave',           desc: 'Leave requests & approvals', tier: 'Starter' },
  { key: 'incidents',    label: 'Incidents',       desc: 'Incident reporting', tier: 'Starter' },
  { key: 'messages',     label: 'Messages',        desc: 'Staff communications', tier: 'Starter' },
  { key: 'documents',    label: 'Documents',       desc: 'Staff & client document storage', tier: 'Starter' },
  { key: 'job_board',    label: 'Job Board',       desc: 'Open shift marketplace', tier: 'Starter' },

  // Growth
  { key: 'schads_payroll', label: 'SCHADS Payroll', desc: 'Award interpretation & pay engine', tier: 'Starter' },
  { key: 'medications',  label: 'Medications',     desc: 'eMAR medication management', tier: 'Growth', defaultOff: true },
  { key: 'gps_tracking', label: 'GPS Tracking',    desc: 'Live location & geofencing', tier: 'Growth', defaultOff: true },
  { key: 'ai_features',  label: 'AI Features',     desc: 'Risk scores, anomaly detection', tier: 'Growth', defaultOff: true },
  { key: 'smart_match',  label: 'Smart Match',     desc: 'AI-powered staff matching', tier: 'Growth', defaultOff: true },
  { key: 'forms',        label: 'Forms Engine',    desc: 'Custom & NDIS form templates', tier: 'Growth', defaultOff: true },
  { key: 'service_agreements', label: 'Service Agreements', desc: 'Agreement generation & e-signature', tier: 'Growth', defaultOff: true },
  { key: 'automations',  label: 'Automations',     desc: 'Shift automations & SMS reminders', tier: 'Growth', defaultOff: true },

  // Scale
  { key: 'group_services', label: 'Group Services', desc: 'Group & SIL shift structures', tier: 'Scale', defaultOff: true },
  { key: 'scheduling_assistant', label: 'Scheduling Assistant', desc: 'AI rostering recommendations', tier: 'Scale', defaultOff: true },
  { key: 'custom_roles', label: 'Custom Roles',    desc: 'Role & permission design', tier: 'Scale', defaultOff: true },
  { key: 'api_access',   label: 'API & MCP Access', desc: 'API, webhooks and the remote MCP server', tier: 'Scale', defaultOff: true },
  { key: 'facilities',   label: 'Facilities',      desc: 'Multi-site / facility register', tier: 'Scale', defaultOff: true },

  // Paid add-on modules
  { key: 'plan_management', label: 'Plan Management', desc: 'Provider invoices & NDIA claiming (PRODA/ABA)', tier: 'Add-on $299', defaultOff: true },
  { key: 'support_coordination', label: 'Support Coordination', desc: 'NDIS Coordination of Supports', tier: 'Add-on $249', defaultOff: true },
  { key: 'aged_care',    label: 'Aged Care',       desc: 'Support at Home / HCP — budgets, claims, DEX/SIRS', tier: 'Add-on $349', defaultOff: true },
  { key: 'ai_receptionist', label: 'AI Receptionist', desc: 'Per-org phone receptionist', tier: 'Add-on $199', defaultOff: true },
  { key: 'ai_chatbot',   label: 'AI Chatbot',      desc: 'Embeddable website chat', tier: 'Add-on $199', defaultOff: true },
  { key: 'google_reviews', label: 'Google Reviews', desc: 'Review requests & reputation monitoring', tier: 'Add-on $79', defaultOff: true },
];

const ONBOARDING_STEPS = [
  { key: 'registered',   label: 'Registration submitted',   auto: true },
  { key: 'verified',     label: 'Email verified',           auto: true },
  { key: 'approved',     label: 'Admin approved',           auto: true },
  { key: 'staff_setup',  label: 'First staff member added', auto: false },
  { key: 'client_setup', label: 'First client added',       auto: false },
  { key: 'first_shift',  label: 'First shift scheduled',    auto: false },
];

const SEVERITY_CFG = {
  info:     { cls: 'bg-blue-100 text-blue-700 border-blue-200',     dot: 'bg-blue-500',     label: 'Info' },
  warning:  { cls: 'bg-amber-100 text-amber-700 border-amber-200',  dot: 'bg-amber-500',    label: 'Warning' },
  critical: { cls: 'bg-red-100 text-red-700 border-red-200',        dot: 'bg-red-500',      label: 'Critical' },
};

// ── Small atoms ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color, sub }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-slate-900">{value ?? '—'}</p>
        <p className="text-sm text-slate-500 leading-snug">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </motion.div>
  );
}

function StatusBadge({ status, suspended }) {
  const b = STATUS_BADGE[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${b.cls}`}>{b.label}</span>
      {suspended && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-600 text-white">Suspended</span>}
    </div>
  );
}

function TabBar({ tabs, active, onChange, className = '' }) {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
            active === t.key ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
          }`}>
          {t.icon && <t.icon size={14} />}
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewTab({ stats, recentLogs, loadingLogs }) {
  const trendData = (stats?.trend || []).map(r => ({ month: r.month, registrations: parseInt(r.count) }));

  return (
    <div className="space-y-6">
      {/* Platform stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Orgs"       value={stats?.orgs?.total}         icon={Building2}   color="bg-indigo-100 text-indigo-600" />
        <StatCard label="Approved Orgs"    value={stats?.orgs?.approved}       icon={CheckCircle} color="bg-emerald-100 text-emerald-600" />
        <StatCard label="Total Users"      value={stats?.platform?.total_users} icon={Users}      color="bg-violet-100 text-violet-600" />
        <StatCard label="Total Clients"    value={stats?.platform?.total_clients} icon={UserCheck} color="bg-cyan-100 text-cyan-600" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Pending Verify"   value={stats?.orgs?.pending_verification} icon={Clock}        color="bg-amber-100 text-amber-600" />
        <StatCard label="Awaiting Approval" value={stats?.orgs?.awaiting_approval}  icon={AlertTriangle} color="bg-blue-100 text-blue-600" />
        <StatCard label="Rejected"         value={stats?.orgs?.rejected}            icon={XCircle}      color="bg-red-100 text-red-600" />
        <StatCard label="Suspended"        value={stats?.orgs?.suspended}           icon={Ban}          color="bg-orange-100 text-orange-600" />
        <StatCard label="New (30d)"        value={stats?.orgs?.new_this_month}      icon={TrendingUp}   color="bg-green-100 text-green-600" />
      </div>

      {/* Trend chart + recent audit */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-slate-700 mb-4">Organisation Registrations (12 months)</h3>
          {trendData.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-slate-400 text-sm">No registration data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={trendData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="regGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Area type="monotone" dataKey="registrations" stroke="#6366f1" strokeWidth={2}
                  fill="url(#regGrad)" dot={{ r: 3, fill: '#6366f1' }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-slate-700 mb-3">Recent Activity</h3>
          {loadingLogs ? (
            <div className="flex items-center justify-center h-40"><Loader2 size={20} className="animate-spin text-slate-300" /></div>
          ) : recentLogs.length === 0 ? (
            <p className="text-slate-400 text-sm text-center mt-8">No activity yet</p>
          ) : (
            <div className="space-y-2.5">
              {recentLogs.slice(0, 8).map(log => (
                <div key={log.id} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-slate-700 leading-snug">
                      <span className="font-semibold text-indigo-600">{log.action}</span>
                      {log.target_name && <> — {log.target_name}</>}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {log.actor_email} · {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Announcements Tab ─────────────────────────────────────────────────────────
function AnnouncementsTab() {
  const confirm = useConfirm();
  const [list, setList]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({ title: '', body: '', severity: 'info', expires_at: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/superadmin/announcements'); setList(data); }
    catch { toast.error('Failed to load announcements'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/superadmin/announcements', form);
      toast.success('Announcement published');
      setForm({ title: '', body: '', severity: 'info', expires_at: '' });
      load();
    } catch { toast.error('Failed to publish'); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!await confirm('Delete this announcement?', { title: 'Delete Announcement', danger: true })) return;
    try { await api.delete(`/superadmin/announcements/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const isExpired = a => a.expires_at && isPast(new Date(a.expires_at));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Create form */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Megaphone size={16} className="text-indigo-500" /> New Announcement</h3>
          <form onSubmit={handleCreate} className="space-y-3">
            <input
              value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Title *" required
              className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
            />
            <textarea
              rows={4} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
              placeholder="Message body *" required
              className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Severity</label>
                <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30">
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Expires (optional)</label>
                <input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30" />
              </div>
            </div>
            <button type="submit" disabled={saving}
              className="w-full py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />}
              Publish Announcement
            </button>
          </form>
        </div>
      </div>

      {/* List */}
      <div className="lg:col-span-3 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-indigo-400" /></div>
        ) : list.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Megaphone size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm">No announcements yet</p>
          </div>
        ) : list.map(a => {
          const sev = SEVERITY_CFG[a.severity] || SEVERITY_CFG.info;
          const expired = isExpired(a);
          return (
            <div key={a.id} className={`bg-white rounded-xl border p-4 ${sev.cls} ${expired ? 'opacity-50' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 flex-1 min-w-0">
                  <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${sev.dot}`} />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm leading-snug">{a.title}
                      {expired && <span className="ml-2 text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded font-normal">Expired</span>}
                    </p>
                    <p className="text-xs mt-1 opacity-80 leading-relaxed">{a.body}</p>
                    <p className="text-[10px] mt-1.5 opacity-60">
                      {a.created_by_email} · {format(new Date(a.created_at), 'dd MMM yyyy')}
                      {a.expires_at && ` · Expires ${format(new Date(a.expires_at), 'dd MMM yyyy')}`}
                    </p>
                  </div>
                </div>
                <button onClick={() => handleDelete(a.id)} className="p-1.5 rounded-lg hover:bg-black/10 transition-colors shrink-0">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Audit Log Tab ─────────────────────────────────────────────────────────────
function AuditLogTab() {
  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [offset, setOffset]   = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/superadmin/audit-log', { params: { search: search || undefined, limit: LIMIT, offset } });
      setLogs(data.logs);
      setTotal(data.total);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  }, [search, offset]);

  useEffect(() => { setOffset(0); }, [search]);
  useEffect(() => { const t = setTimeout(load, search ? 350 : 0); return () => clearTimeout(t); }, [load, search]);

  const ACTION_COLORS = {
    APPROVED: 'text-emerald-600', REJECTED: 'text-red-600', DELETED: 'text-red-700',
    SUSPENDED: 'text-orange-600', UNSUSPENDED: 'text-emerald-600', IMPERSONATED: 'text-violet-600',
    GDPR_WIPED: 'text-red-700', DATA_EXPORTED: 'text-blue-600',
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search action, actor, org…"
            className="w-full pl-8 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><X size={12} /></button>}
        </div>
        <span className="text-xs text-slate-400 ml-auto">{total} entries</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-indigo-400" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16">
          <ScrollText size={32} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">No audit entries</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Timestamp</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actor</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Target</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{format(new Date(log.created_at), 'dd MMM yy h:mm a')}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 font-mono">{log.actor_email || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${ACTION_COLORS[log.action] || 'text-slate-700'}`}>{log.action}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{log.target_name || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-400 font-mono max-w-[200px] truncate">
                      {log.details && Object.keys(log.details).length > 0 ? JSON.stringify(log.details) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <button onClick={() => setOffset(Math.max(0, offset - LIMIT))} disabled={offset === 0}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors">
              Previous
            </button>
            <span className="text-xs text-slate-500">{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
            <button onClick={() => setOffset(offset + LIMIT)} disabled={offset + LIMIT >= total}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors">
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────
function SettingsTab() {
  const [settings, setSettings]   = useState({});
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState('');

  useEffect(() => {
    api.get('/superadmin/settings')
      .then(r => setSettings(r.data))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const saveSetting = async (key, value) => {
    setSaving(key);
    try {
      await api.put('/superadmin/settings', { key, value });
      setSettings(s => ({ ...s, [key]: value }));
      toast.success('Setting saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(''); }
  };

  const maintenanceOn = settings.maintenance_mode === 'true';

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-indigo-400" /></div>;

  return (
    <div className="max-w-2xl space-y-6">
      {/* Maintenance mode */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${maintenanceOn ? 'bg-red-100' : 'bg-slate-100'}`}>
              {maintenanceOn ? <WifiOff size={18} className="text-red-600" /> : <Wifi size={18} className="text-slate-500" />}
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-sm">Maintenance Mode</h3>
              <p className="text-xs text-slate-500">All users see a maintenance page when enabled</p>
            </div>
          </div>
          <button
            onClick={() => saveSetting('maintenance_mode', maintenanceOn ? 'false' : 'true')}
            disabled={saving === 'maintenance_mode'}
            className={`relative w-12 h-6 rounded-full transition-colors ${maintenanceOn ? 'bg-red-500' : 'bg-slate-200'}`}>
            {saving === 'maintenance_mode'
              ? <Loader2 size={12} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin text-white" />
              : <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${maintenanceOn ? 'left-6' : 'left-0.5'}`} />
            }
          </button>
        </div>
        {maintenanceOn && (
          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-xs font-bold text-red-700 flex items-center gap-1.5"><AlertCircle size={12} /> Platform is currently IN MAINTENANCE MODE</p>
          </div>
        )}
        <div className="mt-4">
          <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Maintenance Message</label>
          <textarea
            rows={3} defaultValue={settings.maintenance_message || ''}
            onBlur={e => saveSetting('maintenance_message', e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
            placeholder="Message shown to all users during maintenance…"
          />
          <p className="text-xs text-slate-400 mt-1">Click away to save the message automatically.</p>
        </div>
      </div>

      {/* Platform info */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-bold text-slate-900 text-sm mb-4 flex items-center gap-2"><Globe size={16} className="text-indigo-500" /> Platform Info</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Platform</span><span className="font-semibold text-slate-700">Rostery</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-100">
            <span className="text-slate-500">Version</span><span className="font-semibold text-slate-700">1.0.0</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-slate-500">Powered by</span><span className="font-semibold text-slate-700">Rostery</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Org Drawer ────────────────────────────────────────────────────────────────
function OrgDrawer({ org, onClose, onRefresh }) {
  const { startImpersonation } = useAuth();

  const confirm = useConfirm();
  const [drawerTab, setDrawerTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState('');

  // Local mutable state for each sub-tab
  const [notes, setNotes]         = useState(org.notes || '');
  const [limits, setLimits]       = useState({ max_staff_seats: org.max_staff_seats || 50, max_client_seats: org.max_client_seats || 200 });
  const [trial, setTrial]         = useState(org.trial_ends_at ? org.trial_ends_at.split('T')[0] : '');
  const [branding, setBranding]   = useState(org.branding || {});
  const [onboardingFlags, setOnboardingFlags] = useState(org.onboarding_flags || {});
  const [features, setFeatures]   = useState(() => {
    const saved = org.features || {};
    const d = {};
    // Most modules default ON unless explicitly disabled; opt-in modules
    // (defaultOff, e.g. AI Receptionist) default OFF unless explicitly granted.
    FEATURE_LIST.forEach(f => { d[f.key] = f.defaultOff ? saved[f.key] === true : saved[f.key] !== false; });
    return d;
  });

  const act = async (key, fn) => {
    setActionLoading(key);
    try { await fn(); }
    finally { setActionLoading(''); }
  };

  // Sign in as this organisation for support. The session is audit-logged server side, shows the
  // impersonation banner for its whole duration, and expires in two hours.
  const handleImpersonate = async () => {
    if (!await confirm(
      `Sign in as ${org.name}? You will see their live data — participants, invoices and notes — as one of their admins. This is recorded in the super-admin audit log.`,
      { title: 'Sign in as organisation' })) return;
    await act('impersonate', async () => {
      const { data } = await api.post(`/superadmin/impersonate/${org.id}`);
      startImpersonation(data.token, data.org);
      toast.success(`Now viewing as ${org.name}`);
      onClose();
    });
  };

  const computedOnboarding = {
    registered:   true,
    verified:     !!org.verified_at,
    approved:     !!org.approved_at,
    staff_setup:  !!onboardingFlags.staff_setup,
    client_setup: !!onboardingFlags.client_setup,
    first_shift:  !!onboardingFlags.first_shift,
  };
  const completedCount = Object.values(computedOnboarding).filter(Boolean).length;

  const handleSuspend = async () => {
    const newVal = !org.suspended;
    if (!await confirm(`${newVal ? 'Suspend' : 'Unsuspend'} ${org.name}?`, { title: newVal ? 'Suspend Organisation' : 'Unsuspend Organisation', danger: newVal })) return;
    await act('suspend', async () => {
      await api.put(`/superadmin/organizations/${org.id}/suspend`, { suspended: newVal });
      toast.success(`${org.name} ${newVal ? 'suspended' : 'unsuspended'}`);
      onRefresh();
    });
  };

  const handleSaveNotes = async () => {
    await act('notes', async () => {
      await api.put(`/superadmin/organizations/${org.id}/notes`, { notes });
      toast.success('Notes saved');
    });
  };

  const handleSaveLimits = async () => {
    await act('limits', async () => {
      await api.put(`/superadmin/organizations/${org.id}/limits`, limits);
      toast.success('Limits updated');
      onRefresh();
    });
  };

  const handleSaveTrial = async () => {
    await act('trial', async () => {
      await api.put(`/superadmin/organizations/${org.id}/trial`, { trial_ends_at: trial || null });
      toast.success('Trial period updated');
      onRefresh();
    });
  };

  const extendTrial = async (days) => {
    const base = trial ? new Date(trial) : new Date();
    const newDate = format(addDays(base, days), 'yyyy-MM-dd');
    setTrial(newDate);
    await act('extend-trial', async () => {
      await api.put(`/superadmin/organizations/${org.id}/trial`, { trial_ends_at: newDate });
      toast.success(`Trial extended by ${days} days`);
      onRefresh();
    });
  };

  const handleSaveBranding = async () => {
    await act('branding', async () => {
      await api.put(`/superadmin/organizations/${org.id}/branding`, { branding });
      toast.success('Branding saved');
    });
  };

  const handleSaveFeatures = async () => {
    await act('features', async () => {
      await api.put(`/superadmin/organizations/${org.id}/features`, { features });
      toast.success('Features saved');
    });
  };

  const handleToggleOnboarding = async (key) => {
    const newFlags = { ...onboardingFlags, [key]: !onboardingFlags[key] };
    setOnboardingFlags(newFlags);
    await api.put(`/superadmin/organizations/${org.id}/onboarding`, { flags: newFlags }).catch(() => {});
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/superadmin/organizations/${org.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `${org.name}_export.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handleWipe = async () => {
    if (!await confirm(`GDPR wipe for ${org.name}? This will permanently remove personal contact data. This cannot be undone.`, { title: 'GDPR Data Wipe', danger: true })) return;
    await act('wipe', async () => {
      await api.post(`/superadmin/organizations/${org.id}/wipe`);
      toast.success('Personal data wiped');
      onRefresh();
      onClose();
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40" onClick={onClose} />

      {/* Drawer */}
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden">

        {/* Drawer header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
              <Building2 size={18} className="text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-slate-900 leading-tight truncate">{org.name}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{org.email}</p>
              <div className="mt-1.5">
                <StatusBadge status={org.status} suspended={org.suspended} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {org.status === 'approved' && (
              <button onClick={handleImpersonate} disabled={actionLoading === 'impersonate'}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-100 text-violet-700 text-xs font-semibold hover:bg-violet-200 transition-colors">
                {actionLoading === 'impersonate' ? <Loader2 size={12} className="animate-spin" /> : <LogIn size={12} />}
                Login As
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400"><X size={16} /></button>
          </div>
        </div>

        {/* Drawer sub-tabs */}
        <div className="overflow-x-auto border-b border-slate-100 px-4 py-2 bg-white">
          <div className="flex gap-1 min-w-max">
            {ORG_DRAWER_TABS.map(t => (
              <button key={t.key} onClick={() => setDrawerTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                  drawerTab === t.key ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100'
                }`}>
                <t.icon size={12} />{t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Drawer content */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── Overview ── */}
          {drawerTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['Contact', org.contact_name || '—'],
                  ['Email', org.email],
                  ['Phone', org.phone || '—'],
                  ['ABN', org.abn || '—'],
                  ['Address', org.address || '—'],
                  ['Registered', org.created_at ? format(new Date(org.created_at), 'dd MMM yyyy') : '—'],
                  ['Verified', org.verified_at ? format(new Date(org.verified_at), 'dd MMM yyyy') : '—'],
                  ['Approved By', org.approved_by_name || '—'],
                ].map(([k, v]) => (
                  <div key={k} className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-slate-500">{k}</p>
                    <p className="text-sm text-slate-800 mt-0.5 break-all">{v}</p>
                  </div>
                ))}
              </div>

              {org.rejection_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-xs font-bold text-red-700">Rejection Reason</p>
                  <p className="text-xs text-red-600 mt-1">{org.rejection_reason}</p>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Internal Notes</label>
                <textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Add internal notes…"
                  className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400"
                />
                <button onClick={handleSaveNotes} disabled={actionLoading === 'notes'}
                  className="mt-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition-colors flex items-center gap-1.5 disabled:opacity-60">
                  {actionLoading === 'notes' ? <Loader2 size={12} className="animate-spin" /> : null} Save Notes
                </button>
              </div>
            </div>
          )}

          {/* ── Usage & Limits ── */}
          {drawerTab === 'usage' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-slate-900">{org.max_staff_seats}</p>
                  <p className="text-sm text-slate-500 mt-1">Max Staff Seats</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-slate-900">{org.max_client_seats}</p>
                  <p className="text-sm text-slate-500 mt-1">Max Client Seats</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <h4 className="text-sm font-bold text-slate-800">Edit Seat Limits</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Max Staff</label>
                    <input type="number" min={1} value={limits.max_staff_seats}
                      onChange={e => setLimits(l => ({ ...l, max_staff_seats: parseInt(e.target.value) || 1 }))}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Max Clients</label>
                    <input type="number" min={1} value={limits.max_client_seats}
                      onChange={e => setLimits(l => ({ ...l, max_client_seats: parseInt(e.target.value) || 1 }))}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30" />
                  </div>
                </div>
                <button onClick={handleSaveLimits} disabled={actionLoading === 'limits'}
                  className="w-full py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {actionLoading === 'limits' ? <Loader2 size={14} className="animate-spin" /> : null} Save Limits
                </button>
              </div>

              {/* Feature toggles */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-slate-800">Feature Access</h4>
                  <div className="flex gap-1">
                    <button onClick={() => { const a = {}; FEATURE_LIST.forEach(f => { a[f.key] = true; }); setFeatures(a); }}
                      className="text-[10px] px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 font-bold hover:bg-emerald-200">All On</button>
                    <button onClick={() => { const a = {}; FEATURE_LIST.forEach(f => { a[f.key] = false; }); setFeatures(a); }}
                      className="text-[10px] px-2 py-1 rounded-lg bg-red-100 text-red-700 font-bold hover:bg-red-200">All Off</button>
                  </div>
                </div>
                {/* Grouped by the plan that grants each module, so it is visible at a
                    glance whether an org's toggles match what it pays for. */}
                {[...new Set(FEATURE_LIST.map(f => f.tier))].map(tier => (
                  <div key={tier} className="mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">{tier}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {FEATURE_LIST.filter(f => f.tier === tier).map(({ key, label, desc }) => (
                        <button key={key} title={desc}
                          onClick={() => setFeatures(f => ({ ...f, [key]: !f[key] }))}
                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-all text-left ${
                            features[key] ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                          }`}>
                          <span className="leading-tight">{label}</span>
                          {features[key]
                            ? <ToggleRight size={16} className="text-indigo-500 shrink-0" />
                            : <ToggleLeft size={16} className="text-slate-300 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <button onClick={handleSaveFeatures} disabled={actionLoading === 'features'}
                  className="w-full mt-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {actionLoading === 'features' ? <Loader2 size={14} className="animate-spin" /> : null} Save Features
                </button>
              </div>
            </div>
          )}

          {/* ── Trial ── */}
          {drawerTab === 'trial' && (
            <div className="space-y-4">
              {trial && (
                <div className={`p-4 rounded-xl border ${isPast(new Date(trial)) ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <p className={`text-sm font-bold ${isPast(new Date(trial)) ? 'text-red-700' : 'text-amber-700'}`}>
                    {isPast(new Date(trial)) ? 'Trial EXPIRED' : 'Trial Active'}
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Ends: {format(new Date(trial), 'dd MMMM yyyy')}
                    {!isPast(new Date(trial)) && ` (${formatDistanceToNow(new Date(trial), { addSuffix: true })})`}
                  </p>
                </div>
              )}
              {!trial && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-sm font-bold text-slate-600">No Trial Period Set</p>
                  <p className="text-xs text-slate-400 mt-0.5">Set a date below to enable trial mode for this organisation.</p>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Trial End Date</label>
                <input type="date" value={trial} onChange={e => setTrial(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30" />
              </div>

              <div className="flex gap-2">
                <button onClick={() => extendTrial(14)} disabled={!!actionLoading}
                  className="flex-1 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">+ 14 days</button>
                <button onClick={() => extendTrial(30)} disabled={!!actionLoading}
                  className="flex-1 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">+ 30 days</button>
                <button onClick={() => extendTrial(90)} disabled={!!actionLoading}
                  className="flex-1 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">+ 90 days</button>
              </div>

              <div className="flex gap-2">
                <button onClick={handleSaveTrial} disabled={actionLoading === 'trial'}
                  className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {actionLoading === 'trial' ? <Loader2 size={14} className="animate-spin" /> : null} Save Trial Date
                </button>
                {trial && (
                  <button onClick={() => { setTrial(''); handleSaveTrial(); }} title="Remove trial limit"
                    className="px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-500 hover:bg-slate-50">
                    Clear Trial
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Security ── */}
          {drawerTab === 'security' && (
            <div className="space-y-4">
              <div className={`p-4 rounded-xl border flex items-center justify-between ${org.suspended ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200'}`}>
                <div>
                  <p className="font-bold text-sm text-slate-800">Account Suspension</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {org.suspended ? 'This organisation is currently suspended. All logins are blocked.' : 'Suspend to block all access without deleting.'}
                  </p>
                </div>
                <button onClick={handleSuspend} disabled={actionLoading === 'suspend'}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 ${
                    org.suspended ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-red-500 text-white hover:bg-red-600'
                  }`}>
                  {actionLoading === 'suspend' ? <Loader2 size={14} className="animate-spin" /> : org.suspended ? <Unlock size={14} /> : <Ban size={14} />}
                  {org.suspended ? 'Unsuspend' : 'Suspend'}
                </button>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><LogIn size={14} /> Sign in as organisation</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Opens their account as one of their admins, so you can see what they see when supporting them.
                  You will have access to their live data. The session is written to the audit log, shows a banner
                  throughout, and expires after 2 hours. Only available for approved organisations.
                </p>
                {org.status === 'approved' ? (
                  <button onClick={handleImpersonate} disabled={actionLoading === 'impersonate'}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-100 text-violet-700 text-sm font-semibold hover:bg-violet-200 transition-colors disabled:opacity-60">
                    {actionLoading === 'impersonate' ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                    Login As {org.name}
                  </button>
                ) : (
                  <p className="text-xs text-slate-400 italic">The organisation must be approved first.</p>
                )}
              </div>
            </div>
          )}

          {/* ── Branding ── */}
          {drawerTab === 'branding' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-700 font-semibold">White-Label Branding</p>
                <p className="text-xs text-amber-600 mt-0.5">These settings customise the appearance for this organisation's users.</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Organisation Display Name</label>
                <input type="text" value={branding.display_name || ''} onChange={e => setBranding(b => ({ ...b, display_name: e.target.value }))}
                  placeholder="e.g. Oakwood Care Platform"
                  className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Custom Logo URL</label>
                <input type="url" value={branding.logo_url || ''} onChange={e => setBranding(b => ({ ...b, logo_url: e.target.value }))}
                  placeholder="https://…/logo.png"
                  className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30" />
                {branding.logo_url && <img src={branding.logo_url} alt="Logo preview" className="mt-2 h-10 object-contain rounded" onError={e => e.target.style.display='none'} />}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Primary Brand Colour</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={branding.primary_color || '#6366f1'} onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))}
                    className="w-12 h-10 rounded-xl border border-slate-200 cursor-pointer" />
                  <input type="text" value={branding.primary_color || '#6366f1'} onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))}
                    className="flex-1 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400/30" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block">Email Sender Name</label>
                <input type="text" value={branding.email_sender || ''} onChange={e => setBranding(b => ({ ...b, email_sender: e.target.value }))}
                  placeholder="e.g. Oakwood Platform"
                  className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30" />
              </div>

              <button onClick={handleSaveBranding} disabled={actionLoading === 'branding'}
                className="w-full py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {actionLoading === 'branding' ? <Loader2 size={14} className="animate-spin" /> : <Palette size={14} />} Save Branding
              </button>
            </div>
          )}

          {/* ── Onboarding ── */}
          {drawerTab === 'onboarding' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-slate-800">Onboarding Progress</h4>
                <span className="text-xs font-bold text-indigo-600">{completedCount}/{ONBOARDING_STEPS.length} complete</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2 mb-4">
                <div className="bg-indigo-500 h-2 rounded-full transition-all" style={{ width: `${(completedCount / ONBOARDING_STEPS.length) * 100}%` }} />
              </div>

              <div className="space-y-2">
                {ONBOARDING_STEPS.map((step, i) => {
                  const done = computedOnboarding[step.key];
                  return (
                    <div key={step.key} className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all ${done ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                        {done ? <CheckCircle size={14} /> : i + 1}
                      </div>
                      <p className={`text-sm font-semibold flex-1 ${done ? 'text-emerald-700' : 'text-slate-600'}`}>{step.label}</p>
                      {!step.auto && (
                        <button onClick={() => handleToggleOnboarding(step.key)}
                          className="text-xs px-2.5 py-1 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors">
                          {done ? 'Mark Incomplete' : 'Mark Done'}
                        </button>
                      )}
                      {step.auto && <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Auto</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── GDPR ── */}
          {drawerTab === 'gdpr' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm font-bold text-blue-800 flex items-center gap-2"><Database size={14} /> GDPR & Data Controls</p>
                <p className="text-xs text-blue-700 mt-1">These tools allow you to fulfil data subject rights requests under the Australian Privacy Act and GDPR.</p>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h4 className="text-sm font-bold text-slate-800 mb-1">Export Organisation Data</h4>
                <p className="text-xs text-slate-500 mb-3">Downloads a CSV of all registration and configuration data for this organisation. Use to fulfil a right-of-access request.</p>
                <button onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-100 text-blue-700 text-sm font-semibold hover:bg-blue-200 transition-colors">
                  <Download size={14} /> Download Data Export (CSV)
                </button>
              </div>

              <div className="bg-white border border-red-200 rounded-xl p-4">
                <h4 className="text-sm font-bold text-red-800 mb-1 flex items-center gap-2"><AlertTriangle size={14} /> Wipe Personal Data</h4>
                <p className="text-xs text-red-600 mb-3">
                  Permanently removes personal contact information (contact name, phone, address, notes) from this org's registration record.
                  The organisation account itself is retained. <strong>This cannot be undone.</strong>
                </p>
                <button onClick={handleWipe} disabled={actionLoading === 'wipe'}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-60">
                  {actionLoading === 'wipe' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Wipe Personal Data (GDPR)
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ── Orgs Tab ──────────────────────────────────────────────────────────────────
function OrgsTab({ orgs, loadingOrgs, onSelectOrg, onApprove, onReject, onRevoke, onDelete, onRefresh, actionLoading }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]             = useState('');

  const filtered = orgs.filter(o => {
    if (statusFilter && o.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return o.name?.toLowerCase().includes(q) || o.email?.toLowerCase().includes(q);
    }
    return true;
  });

  const fmtDate = d => d ? format(new Date(d), 'dd MMM yyyy') : '—';

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="overflow-x-auto pb-1">
            <TabBar tabs={STATUS_TABS} active={statusFilter} onChange={setStatusFilter} />
          </div>
          <div className="relative w-full sm:w-72 shrink-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="w-full pl-8 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400"><X size={12} /></button>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loadingOrgs ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-indigo-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Building2 size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">No organisations found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Organisation</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Contact / ABN</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Features</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Trial</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(org => {
                  const feats = org.features || {};
                  const enabledCount = FEATURE_LIST.filter(f => f.defaultOff ? feats[f.key] === true : feats[f.key] !== false).length;
                  const isApproving = actionLoading === `approve-${org.id}`;
                  const isRejecting = actionLoading === `reject-${org.id}`;
                  const isDeleting  = actionLoading === `delete-${org.id}`;
                  const isRevoking  = actionLoading === `revoke-${org.id}`;
                  const trialExpired = org.trial_ends_at && isPast(new Date(org.trial_ends_at));

                  return (
                    <tr key={org.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <button onClick={() => onSelectOrg(org)} className="text-left group">
                          <p className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors flex items-center gap-1">
                            {org.name} <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                          </p>
                          <p className="text-slate-400 text-xs">{org.email}</p>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-slate-600 text-xs">{org.contact_name || '—'}</p>
                        <p className="text-slate-400 text-[11px] font-mono">{org.abn || '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => onSelectOrg(org)}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition-colors">
                          <Sliders size={10} /> {enabledCount}/{FEATURE_LIST.length}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {org.trial_ends_at ? (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${trialExpired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {trialExpired ? 'Expired' : format(new Date(org.trial_ends_at), 'dd MMM')}
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={org.status} suspended={org.suspended} />
                        {org.rejection_reason && (
                          <p className="text-red-400 text-[10px] mt-0.5 max-w-[140px] truncate" title={org.rejection_reason}>{org.rejection_reason}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end flex-wrap">
                          {/* Details */}
                          <button onClick={() => onSelectOrg(org)} title="View Details"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">
                            <FileText size={14} />
                          </button>

                          {(org.status === 'verified' || org.status === 'pending_verification') && (
                            <>
                              <button onClick={() => onApprove(org)} disabled={isApproving}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 disabled:opacity-60 transition-colors">
                                {isApproving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle size={11} />} Approve
                              </button>
                              <button onClick={() => onReject(org)} disabled={isRejecting}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 disabled:opacity-60 transition-colors">
                                <XCircle size={11} /> Reject
                              </button>
                            </>
                          )}

                          {org.status === 'approved' && (
                            <button onClick={() => onRevoke(org)} disabled={isRevoking}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-300 disabled:opacity-60 transition-colors">
                              {isRevoking ? <Loader2 size={11} className="animate-spin" /> : <XCircle size={11} />} Revoke
                            </button>
                          )}

                          <button onClick={() => onDelete(org)} disabled={isDeleting}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-60 transition-colors">
                            {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Reject Modal ───────────────────────────────────────────────────────────────
function RejectModal({ org, onClose, onConfirm, loading }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: .95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-900">Reject Organisation</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>
        <p className="text-sm text-slate-600 mb-4">You are rejecting <strong>{org.name}</strong>. Provide a reason — this will be emailed to the applicant.</p>
        <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
          placeholder="e.g. Unable to verify ABN, incomplete information…"
          className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400/30 focus:border-red-400" />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
          <button onClick={() => onConfirm(reason)} disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Reject
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function SuperAdminDashboard() {
  const confirm = useConfirm();
  const [mainTab, setMainTab]         = useState('overview');
  const [stats, setStats]             = useState(null);
  const [orgs, setOrgs]               = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [recentLogs, setRecentLogs]   = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [actionLoading, setActionLoading] = useState('');

  const fetchStats = useCallback(async () => {
    try { const { data } = await api.get('/superadmin/stats'); setStats(data); } catch { /* silent */ }
  }, []);

  const fetchOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    try { const { data } = await api.get('/superadmin/organizations'); setOrgs(data); }
    catch { toast.error('Failed to load organisations'); }
    finally { setLoadingOrgs(false); }
  }, []);

  const fetchRecentLogs = useCallback(async () => {
    setLoadingLogs(true);
    try { const { data } = await api.get('/superadmin/audit-log', { params: { limit: 8, offset: 0 } }); setRecentLogs(data.logs); }
    catch { /* silent */ }
    finally { setLoadingLogs(false); }
  }, []);

  useEffect(() => { fetchStats(); fetchOrgs(); fetchRecentLogs(); }, [fetchStats, fetchOrgs, fetchRecentLogs]);

  const refreshAll = () => { fetchStats(); fetchOrgs(); fetchRecentLogs(); };

  const handleApprove = async (org) => {
    setActionLoading(`approve-${org.id}`);
    try { await api.put(`/superadmin/organizations/${org.id}/approve`); toast.success(`${org.name} approved`); refreshAll(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const handleRejectOpen = (org) => setRejectModal(org);

  const handleRejectConfirm = async (reason) => {
    const org = rejectModal;
    setActionLoading(`reject-${org.id}`);
    try {
      await api.put(`/superadmin/organizations/${org.id}/reject`, { reason });
      toast.success(`${org.name} rejected`);
      setRejectModal(null);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const handleRevoke = async (org) => {
    if (!await confirm(`Revoke access for ${org.name}?`, { title: 'Revoke Access', danger: true })) return;
    setActionLoading(`revoke-${org.id}`);
    try { await api.put(`/superadmin/organizations/${org.id}/reject`, { reason: 'Access revoked by administrator' }); toast.success('Revoked'); refreshAll(); }
    catch { toast.error('Failed'); }
    finally { setActionLoading(''); }
  };

  const handleDelete = async (org) => {
    if (!await confirm(`Permanently delete ${org.name}? This cannot be undone.`, { title: 'Delete Organisation', danger: true })) return;
    setActionLoading(`delete-${org.id}`);
    try { await api.delete(`/superadmin/organizations/${org.id}`); toast.success(`${org.name} deleted`); refreshAll(); }
    catch { toast.error('Failed'); }
    finally { setActionLoading(''); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Shield size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Super Admin</h1>
              <p className="text-sm text-slate-500">Platform management &amp; organisation controls</p>
            </div>
          </div>
          <button onClick={refreshAll} className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50 transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Main tabs */}
        <div className="bg-white rounded-xl border border-slate-200 p-1.5">
          <div className="overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {MAIN_TABS.map(t => (
                <button key={t.key} onClick={() => setMainTab(t.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                    mainTab === t.key ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                  }`}>
                  <t.icon size={15} />{t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          <motion.div key={mainTab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: .18 }}>
            {mainTab === 'overview' && <OverviewTab stats={stats} recentLogs={recentLogs} loadingLogs={loadingLogs} />}
            {mainTab === 'orgs' && (
              <OrgsTab
                orgs={orgs}
                loadingOrgs={loadingOrgs}
                actionLoading={actionLoading}
                onSelectOrg={setSelectedOrg}
                onApprove={handleApprove}
                onReject={handleRejectOpen}
                onRevoke={handleRevoke}
                onDelete={handleDelete}
                onRefresh={refreshAll}
              />
            )}
            {mainTab === 'announcements' && <AnnouncementsTab />}
            {mainTab === 'audit' && <AuditLogTab />}
            {mainTab === 'billing' && <BillingTab />}
            {mainTab === 'settings' && <SettingsTab />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Org Drawer */}
      <AnimatePresence>
        {selectedOrg && (
          <OrgDrawer
            org={selectedOrg}
            onClose={() => setSelectedOrg(null)}
            onRefresh={() => { fetchOrgs(); fetchStats(); }}
          />
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {rejectModal && (
          <RejectModal
            org={rejectModal}
            onClose={() => setRejectModal(null)}
            onConfirm={handleRejectConfirm}
            loading={actionLoading === `reject-${rejectModal.id}`}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
